import * as React from "react";

import { AsyncComponent } from "@impero-com/common/components/asyncComponent";
import {
    InputWithTypeahead,
    TypeaheadOptionOrGroup,
} from "@impero-com/common/components/typeahead";
import { LoggedInHTTPError } from "@impero-com/common/errorPages/loggedInHTTPError";
import { AjaxManager } from "@impero-com/common/utils/ajax";
import { getParentElement } from "@impero-com/common/utils/dom";
import { formatUserName } from "@impero-com/common/utils/formatting";
import { t } from "@impero-com/common/utils/i18n";
import { Button, PageSpinner, Svg } from "@impero-com/ui";
import RemoveIconSvg from "@impero-com/ui/img/remove.svg";
import _ from "lodash-es";

import {
    UserGroupViewDto as UserGroupWithDetails,
    UserOverview,
    UserView,
} from "api/generated/model";
import { getUserGroupsGroupId } from "api/generated/user-groups/user-groups";
import { BaseFormDisplay } from "components/form/legacyFields/formDisplay";

import * as theme from "./theme.css";

class DefaultClaimantSelectorAsync extends React.PureComponent<DefaultClaimantSelectorPropsAsync> {
    render = () => {
        const { userGroupId, defaultClaimantId, ajaxManager, onChange, error } =
            this.props;
        return (
            <AsyncComponent<UserGroupWithDetails>
                key={userGroupId.toString()}
                ajaxManager={ajaxManager}
                promise={() =>
                    getUserGroupsGroupId(userGroupId).then((response) => ({
                        ...response,
                        data: response.data,
                    }))
                }
                renderError={(code) => <LoggedInHTTPError code={code} />}
                renderLoading={() => <PageSpinner />}
                renderContent={(userGroupWithDetails) => {
                    const users = _.chain(
                        userGroupWithDetails.content.resource.userGroupUsers
                    )
                        .uniqBy((ugu) => ugu.userId)
                        .map(
                            (ugu) =>
                                _.find(
                                    userGroupWithDetails.linkedResources.users,
                                    (u) => u.resource.id === ugu.userId
                                )!.resource
                        )
                        .map((u: UserView) => {
                            const { id, email, lastname, firstname } = u;
                            const overview: UserOverview = {
                                id,
                                email,
                                lastname,
                                firstname,
                            };
                            return overview;
                        })
                        .sortBy((u) => formatUserName(u).toLowerCase())
                        .value();
                    return (
                        <DefaultClaimantSelector
                            {...{ users, defaultClaimantId, error }}
                            onSelect={onChange}
                        />
                    );
                }}
            />
        );
    };
}

interface DefaultClaimantSelectorPropsAsync {
    ajaxManager: AjaxManager;
    userGroupId: number;
    defaultClaimantId: number | undefined;
    onChange: (defaultClaimant: UserOverview | undefined) => void;
    error?: string;
}

class DefaultClaimantSelector extends React.PureComponent<DefaultClaimantSelectorProps> {
    render = () => {
        const { defaultClaimantId, users, onSelect, error } = this.props;
        const defaultClaimantName =
            defaultClaimantId !== undefined
                ? formatUserName(users.find((u) => u.id === defaultClaimantId))
                : undefined;
        return (
            <BaseFormDisplay
                label={t("CreateControl_Assignees_DefaultClaimant_Title")}
                helpText={t("CreateControl_Assignees_DefaultClaimant_Tooltip")}
                error={error}
                className={theme.assigneeField}
            >
                <div className={theme.defaultClaimantContainer}>
                    <InputWithTypeahead<UserOverview>
                        id="default-claimant-id"
                        className={theme.defaultClaimantTypeahead}
                        value={
                            defaultClaimantName !== null
                                ? defaultClaimantName
                                : undefined
                        }
                        onSelect={onSelect}
                        formatOption={formatUserName}
                        onSearch={this.handleSearch}
                        // Same as the other drawer typeaheads: keep the popup
                        // inside the drawer's scroll container
                        getPopupContainer={getParentElement}
                    />
                    <Button
                        id="delete-default-claimant-button"
                        variant="link"
                        onClick={this.handleRemove}
                        className={theme.removeButton}
                    >
                        <Svg className={theme.removeIcon} src={RemoveIconSvg} />
                    </Button>
                </div>
            </BaseFormDisplay>
        );
    };

    private handleSearch = (
        searchValue: string,
        callback: (
            optionOrGroups: Array<TypeaheadOptionOrGroup<UserOverview>>
        ) => void
    ): Promise<void> => {
        const { users } = this.props;
        const lcValue = searchValue.toLowerCase();
        callback(
            _.chain(users)
                .filter(
                    (user) =>
                        formatUserName(user).toLowerCase().indexOf(lcValue) >=
                            0 || user.email.toLowerCase().indexOf(lcValue) >= 0
                )
                .map((user) => {
                    return {
                        type: "Option" as const,
                        value: user,
                    };
                })
                .value()
        );
        return Promise.resolve();
    };

    private handleRemove = () => {
        const { defaultClaimantId, onSelect } = this.props;
        if (defaultClaimantId !== undefined) {
            onSelect(undefined);
        }
    };
}

interface DefaultClaimantSelectorProps {
    users: UserOverview[];
    defaultClaimantId: number | undefined;
    onSelect: (defaultClaimant: UserOverview | undefined) => void;
    error?: string;
}

export { DefaultClaimantSelectorAsync };
